import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Phone, MapPin, Users, Calendar, FileText, Upload, Truck,
  AlertTriangle, Loader2, Trash2, Camera, Sparkles, Eye, Image, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import { PhoneWithWhatsApp } from '../components/WhatsAppLink';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

const TIPI_DOCUMENTO = [
  { value: 'carta_identita', label: "Carta d'identita" },
  { value: 'codice_fiscale', label: 'Codice fiscale' },
  { value: 'stato_famiglia', label: 'Stato di famiglia' },
  { value: 'isee', label: 'ISEE' },
  { value: 'permesso_soggiorno', label: 'Permesso di soggiorno' },
  { value: 'documenti_sanitari', label: 'Documenti sanitari' },
  { value: 'domanda_banco', label: 'Domanda banco alimentare' },
  { value: 'consenso_privacy', label: 'Consenso privacy' },
];

export const FamigliaDetailPage = () => {
  const { id } = useParams();
  const { API, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [famiglia, setFamiglia] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [storico, setStorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({ tipo_documento: '', scadenza: '', note: '', file: null });
  const [filePreview, setFilePreview] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(null); // docId being analyzed
  const [aiResult, setAiResult] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessData, setAccessData] = useState({ email: '', password: '' });
  const [creatingAccess, setCreatingAccess] = useState(false);

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const [famigliaRes, documentiRes, storicoRes] = await Promise.all([
        axios.get(`${API}/famiglie/${id}`),
        axios.get(`${API}/famiglie/${id}/documenti`),
        axios.get(`${API}/famiglie/${id}/storico`)
      ]);
      setFamiglia(famigliaRes.data);
      setDocumenti(documentiRes.data);
      setStorico(storicoRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento dei dati');
      navigate('/famiglie');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/pdf/richiesta-banco/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `richiesta_banco_${famiglia?.codice_famiglia || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nel generare il PDF'));
    }
  };

  const handleCreateAccess = async () => {
    if (!accessData.email || !accessData.password) {
      toast.error('Email e password obbligatorie');
      return;
    }
    setCreatingAccess(true);
    try {
      await axios.post(`${API}/famiglie/${id}/crea-accesso`, accessData);
      toast.success('Accesso creato! La famiglia può ora accedere al portale.');
      setAccessDialogOpen(false);
      setAccessData({ email: '', password: '' });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Errore nella creazione dell'accesso"));
    } finally {
      setCreatingAccess(false);
    }
  };


  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadData(prev => ({ ...prev, file }));
    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.tipo_documento) { toast.error('Seleziona un tipo documento e un file'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('tipo_documento', uploadData.tipo_documento);
    if (uploadData.scadenza) formData.append('scadenza', uploadData.scadenza);
    if (uploadData.note) formData.append('note', uploadData.note);
    try {
      await axios.post(`${API}/famiglie/${id}/documenti`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Documento caricato');
      setUploadOpen(false);
      setUploadData({ tipo_documento: '', scadenza: '', note: '', file: null });
      setFilePreview(null);
      fetchData();
    } catch (err) {
      toast.error('Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleAiAnalyze = async (docId) => {
    setAiAnalyzing(docId);
    try {
      const response = await axios.post(`${API}/documenti/${docId}/analizza-ai`);
      setAiResult(response.data);
      setAiDialogOpen(true);
    } catch (err) {
      toast.error("Errore nell'analisi AI");
    } finally {
      setAiAnalyzing(null);
    }
  };

  const handleApplyAiData = async () => {
    if (!aiResult || !aiResult.success) return;
    const updateData = {};
    if (aiResult.nome_referente) updateData.nome_referente = aiResult.nome_referente;
    if (aiResult.valore_isee) updateData.isee = aiResult.valore_isee;
    if (aiResult.scadenza_isee) updateData.scadenza_isee = aiResult.scadenza_isee;
    if (aiResult.numero_adulti !== null && aiResult.numero_adulti !== undefined) updateData.numero_adulti = aiResult.numero_adulti;
    if (aiResult.bambini_0_3 !== null && aiResult.bambini_0_3 !== undefined) updateData.bambini_0_3 = aiResult.bambini_0_3;
    if (aiResult.bambini_4_5 !== null && aiResult.bambini_4_5 !== undefined) updateData.bambini_4_5 = aiResult.bambini_4_5;
    if (aiResult.bambini_6_12 !== null && aiResult.bambini_6_12 !== undefined) updateData.bambini_6_12 = aiResult.bambini_6_12;
    if (aiResult.ragazzi_13_17 !== null && aiResult.ragazzi_13_17 !== undefined) updateData.ragazzi_13_17 = aiResult.ragazzi_13_17;
    if (aiResult.anziani !== null && aiResult.anziani !== undefined) updateData.anziani = aiResult.anziani;
    if (aiResult.disabili !== null && aiResult.disabili !== undefined) updateData.disabili = aiResult.disabili;
    if (aiResult.indirizzo) updateData.indirizzo = aiResult.indirizzo;
    if (aiResult.comune) updateData.comune = aiResult.comune;

    try {
      await axios.put(`${API}/famiglie/${id}`, { ...famiglia, ...updateData });
      toast.success('Dati famiglia aggiornati con AI');
      setAiDialogOpen(false);
      setAiResult(null);
      fetchData();
    } catch (err) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API}/documenti/${docId}`);
      toast.success('Documento eliminato');
      fetchData();
    } catch (err) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const getTotaleValore = () => storico.reduce((sum, d) => sum + (d.valore_totale || 0), 0);

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 sm:h-64 w-full" />
      </div>
    );
  }

  if (!famiglia) return null;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="famiglia-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/famiglie')} className="h-10 w-10 flex-shrink-0" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-heading font-bold truncate">{famiglia.nome_referente}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground font-mono">{famiglia.codice_famiglia}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-14">
          <Badge variant={famiglia.stato_pratica === 'attivo' ? 'default' : 'secondary'}>
            {famiglia.stato_pratica}
          </Badge>
          <Badge variant={famiglia.priorita_sociale === 'alta' ? 'destructive' : 'outline'}>
            Priorita {famiglia.priorita_sociale}
          </Badge>
          <Button size="sm" onClick={() => navigate(`/distribuzioni/nuova?famiglia=${id}`)} className="ml-auto" data-testid="new-distribution-btn">
            <Truck className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Nuova </span>Distribuzione
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <FileDown className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Richiesta </span>PDF
          </Button>
          {!famiglia.ha_accesso && isAdmin() && (
            <Button size="sm" variant="outline" onClick={() => setAccessDialogOpen(true)} data-testid="create-access-btn">
              <Users className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Crea </span>Accesso
            </Button>
          )}
          {famiglia.ha_accesso && (
            <Badge variant="outline" className="text-green-600 border-green-600 text-xs">Ha accesso portale</Badge>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        {/* Contatti */}
        <Card>
          <CardHeader className="pb-2 px-4 pt-4 sm:px-6 sm:pt-6">
            <CardTitle className="text-sm sm:text-base">Contatti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              {famiglia.telefono ? (
                <PhoneWithWhatsApp phone={famiglia.telefono} message={`Ciao ${famiglia.nome_referente}, ti contattiamo dal banco alimentare.`} />
              ) : (
                <span className="text-xs text-muted-foreground">Non specificato</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm truncate">{famiglia.indirizzo || 'Non specificato'}{famiglia.comune ? `, ${famiglia.comune}` : ''}</span>
            </div>
          </CardContent>
        </Card>

        {/* Composizione */}
        <Card>
          <CardHeader className="pb-2 px-4 pt-4 sm:px-6 sm:pt-6">
            <CardTitle className="text-sm sm:text-base">Nucleo Familiare</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="text-xl font-bold">{famiglia.totale_componenti}</span>
              <span className="text-xs text-muted-foreground">persone</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              <div>Adulti: <span className="font-medium">{famiglia.numero_adulti}</span></div>
              <div>0-3: <span className="font-medium">{famiglia.bambini_0_3}</span></div>
              <div>4-5: <span className="font-medium">{famiglia.bambini_4_5}</span></div>
              <div>6-12: <span className="font-medium">{famiglia.bambini_6_12}</span></div>
              <div>13-17: <span className="font-medium">{famiglia.ragazzi_13_17}</span></div>
              <div>Anziani: <span className="font-medium">{famiglia.anziani}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Situazione */}
        <Card className="sm:col-span-2 lg:col-span-1">
          <CardHeader className="pb-2 px-4 pt-4 sm:px-6 sm:pt-6">
            <CardTitle className="text-sm sm:text-base">Situazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 px-4 pb-4 sm:px-6 sm:pb-6">
            {famiglia.isee > 0 && (
              <div className="text-xs sm:text-sm">
                ISEE: <span className="font-medium">{famiglia.isee.toLocaleString()}</span>
                {famiglia.scadenza_isee && (
                  <span className="text-muted-foreground ml-1">(scade {new Date(famiglia.scadenza_isee).toLocaleDateString('it-IT')})</span>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {famiglia.famiglia_monogenitoriale && <Badge variant="secondary" className="text-[10px]">Monogenitoriale</Badge>}
              {famiglia.disoccupazione && <Badge variant="secondary" className="text-[10px]">Disoccupazione</Badge>}
              {famiglia.emergenza_sociale && <Badge variant="destructive" className="text-[10px]">Emergenza</Badge>}
              {famiglia.presenza_neonati && <Badge variant="secondary" className="text-[10px]">Neonati</Badge>}
            </div>
            {famiglia.note_operatore && (
              <p className="text-xs text-muted-foreground p-2 bg-muted rounded">{famiglia.note_operatore}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="storico" className="w-full">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="storico" className="flex-1 sm:flex-none" data-testid="tab-storico">Storico Aiuti</TabsTrigger>
          <TabsTrigger value="documenti" className="flex-1 sm:flex-none" data-testid="tab-documenti">Documenti</TabsTrigger>
        </TabsList>

        <TabsContent value="storico" className="mt-4 sm:mt-6">
          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-sm sm:text-base">Storico Distribuzioni</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {storico.length} distribuzioni - Valore totale: {getTotaleValore().toFixed(2)}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 sm:px-6 sm:pb-6">
              {storico.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-muted-foreground">
                  <Truck className="w-10 h-10 mx-auto opacity-50" />
                  <p className="mt-3 text-sm">Nessuna distribuzione registrata</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Prodotti</TableHead>
                          <TableHead>Operatore</TableHead>
                          <TableHead className="text-right">Valore</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {storico.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell>
                              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" />{new Date(d.data).toLocaleDateString('it-IT')}</div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {d.prodotti.slice(0, 3).map((p, i) => <div key={i} className="text-sm">{p.nome_prodotto} x {p.quantita}</div>)}
                                {d.prodotti.length > 3 && <span className="text-xs text-muted-foreground">+{d.prodotti.length - 3} altri</span>}
                              </div>
                            </TableCell>
                            <TableCell>{d.operatore_nome}</TableCell>
                            <TableCell className="text-right font-medium">{(d.valore_totale || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Mobile Card List */}
                  <div className="md:hidden">
                    {storico.map((d) => (
                      <div key={d.id} className="mobile-card-item">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="w-3 h-3" />{new Date(d.data).toLocaleDateString('it-IT')}
                            </div>
                            <div className="mt-1 space-y-0.5">
                              {d.prodotti.slice(0, 2).map((p, i) => <p key={i} className="text-xs">{p.nome_prodotto} x {p.quantita}</p>)}
                              {d.prodotti.length > 2 && <p className="text-[10px] text-muted-foreground">+{d.prodotti.length - 2} altri</p>}
                            </div>
                          </div>
                          <span className="font-medium text-sm flex-shrink-0">{(d.valore_totale || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documenti" className="mt-4 sm:mt-6">
          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-sm sm:text-base">Documenti</CardTitle>
                  <CardDescription className="text-xs">{documenti.length} documenti caricati</CardDescription>
                </div>
                {isAdmin() && (
                  <Dialog open={uploadOpen} onOpenChange={(open) => { setUploadOpen(open); if (!open) { setFilePreview(null); setUploadData({ tipo_documento: '', scadenza: '', note: '', file: null }); } }}>
                    <Button size="sm" onClick={() => setUploadOpen(true)} className="w-full sm:w-auto" data-testid="upload-doc-btn">
                      <Upload className="w-4 h-4 mr-1" />
                      Carica Documento
                    </Button>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader><DialogTitle>Carica Documento</DialogTitle></DialogHeader>
                      <form onSubmit={handleUpload} className="space-y-4 max-h-[70vh] overflow-y-auto">
                        <div className="space-y-2">
                          <Label>Tipo Documento *</Label>
                          <Select value={uploadData.tipo_documento} onValueChange={(v) => setUploadData({ ...uploadData, tipo_documento: v })}>
                            <SelectTrigger data-testid="doc-tipo-select"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                            <SelectContent>{TIPI_DOCUMENTO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>

                        {/* File input with camera capture */}
                        <div className="space-y-2">
                          <Label>Documento *</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div className="relative">
                              <Input
                                type="file"
                                accept="image/*,.pdf"
                                capture="environment"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="camera-input"
                                data-testid="doc-camera-input"
                              />
                              <label htmlFor="camera-input" className="flex items-center justify-center gap-2 h-11 px-4 rounded-md border border-input bg-background cursor-pointer hover:bg-accent transition-colors text-sm">
                                <Camera className="w-4 h-4" />
                                Scatta Foto
                              </label>
                            </div>
                            <div className="relative">
                              <Input
                                type="file"
                                accept="image/*,.pdf,.doc,.docx"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="file-input"
                                data-testid="doc-file-input"
                              />
                              <label htmlFor="file-input" className="flex items-center justify-center gap-2 h-11 px-4 rounded-md border border-input bg-background cursor-pointer hover:bg-accent transition-colors text-sm">
                                <Upload className="w-4 h-4" />
                                Scegli File
                              </label>
                            </div>
                          </div>
                          {uploadData.file && (
                            <p className="text-xs text-muted-foreground truncate">
                              Selezionato: {uploadData.file.name}
                            </p>
                          )}
                        </div>

                        {/* Image preview */}
                        {filePreview && (
                          <div className="rounded-lg overflow-hidden border bg-muted">
                            <img src={filePreview} alt="Preview" className="w-full max-h-48 object-contain" />
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Data Scadenza</Label>
                            <Input type="date" value={uploadData.scadenza} onChange={(e) => setUploadData({ ...uploadData, scadenza: e.target.value })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Note</Label>
                            <Input value={uploadData.note} onChange={(e) => setUploadData({ ...uploadData, note: e.target.value })} />
                          </div>
                        </div>

                        <DialogFooter className="flex-col sm:flex-row gap-2">
                          <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} className="w-full sm:w-auto">Annulla</Button>
                          <Button type="submit" disabled={uploading || !uploadData.file} className="w-full sm:w-auto" data-testid="doc-submit-btn">
                            {uploading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Carica
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 sm:px-6 sm:pb-6">
              {documenti.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto opacity-50" />
                  <p className="mt-3 text-sm">Nessun documento caricato</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Data Caricamento</TableHead>
                          <TableHead>Scadenza</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {documenti.map((doc) => {
                          const tipoLabel = TIPI_DOCUMENTO.find(t => t.value === doc.tipo_documento)?.label || doc.tipo_documento;
                          const isExpired = doc.scadenza && new Date(doc.scadenza) < new Date();
                          const isImage = doc.content_type?.startsWith('image/') || doc.filename?.match(/\.(jpg|jpeg|png|webp)$/i);
                          return (
                            <TableRow key={doc.id}>
                              <TableCell><div className="flex items-center gap-2">{isImage ? <Image className="w-4 h-4 text-muted-foreground" /> : <FileText className="w-4 h-4 text-muted-foreground" />}{tipoLabel}</div></TableCell>
                              <TableCell>{new Date(doc.data_caricamento).toLocaleDateString('it-IT')}</TableCell>
                              <TableCell>
                                {doc.scadenza ? (
                                  <div className="flex items-center gap-1">
                                    {isExpired && <AlertTriangle className="w-4 h-4 text-destructive" />}
                                    <span className={isExpired ? 'text-destructive' : ''}>{new Date(doc.scadenza).toLocaleDateString('it-IT')}</span>
                                  </div>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Badge variant={isExpired ? 'destructive' : 'outline'}>{isExpired ? 'Scaduto' : doc.stato_documento}</Badge>
                                  {doc.ai_analizzato && <Badge variant="secondary" className="text-[10px]">AI</Badge>}
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {isImage && (
                                    <Button variant="ghost" size="icon" onClick={() => setPreviewDoc(doc)} title="Anteprima"><Eye className="w-4 h-4" /></Button>
                                  )}
                                  <Button variant="ghost" size="icon" onClick={() => handleAiAnalyze(doc.id)} disabled={aiAnalyzing === doc.id} title="Analizza con AI" data-testid={`ai-analyze-${doc.id}`}>
                                    {aiAnalyzing === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                  </Button>
                                  {isAdmin() && (
                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)} className="text-destructive" data-testid={`delete-doc-${doc.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Mobile Card List */}
                  <div className="md:hidden">
                    {documenti.map((doc) => {
                      const tipoLabel = TIPI_DOCUMENTO.find(t => t.value === doc.tipo_documento)?.label || doc.tipo_documento;
                      const isExpired = doc.scadenza && new Date(doc.scadenza) < new Date();
                      const isImage = doc.content_type?.startsWith('image/') || doc.filename?.match(/\.(jpg|jpeg|png|webp)$/i);
                      return (
                        <div key={doc.id} className="mobile-card-item">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {isImage ? <Image className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" /> : <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                                <span className="font-medium text-sm">{tipoLabel}</span>
                                <Badge variant={isExpired ? 'destructive' : 'outline'} className="text-[10px] px-1.5 py-0">
                                  {isExpired ? 'Scaduto' : doc.stato_documento}
                                </Badge>
                                {doc.ai_analizzato && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">AI</Badge>}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                <span>Caricato: {new Date(doc.data_caricamento).toLocaleDateString('it-IT')}</span>
                                {doc.scadenza && (
                                  <span className={isExpired ? 'text-destructive font-medium' : ''}>
                                    Scade: {new Date(doc.scadenza).toLocaleDateString('it-IT')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {isImage && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreviewDoc(doc)}><Eye className="w-3.5 h-3.5" /></Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleAiAnalyze(doc.id)} disabled={aiAnalyzing === doc.id}>
                                {aiAnalyzing === doc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                              </Button>
                              {isAdmin() && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteDoc(doc.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {previewDoc && (TIPI_DOCUMENTO.find(t => t.value === previewDoc.tipo_documento)?.label || previewDoc.tipo_documento)}
            </DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="rounded-lg overflow-hidden bg-muted">
              <img
                src={`${API}/documenti/${previewDoc.id}/file`}
                alt="Documento"
                className="w-full max-h-[70vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Analysis Results Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Risultato Analisi AI
            </DialogTitle>
          </DialogHeader>
          {aiResult && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {!aiResult.success ? (
                <div className="p-4 bg-destructive/10 rounded-lg text-sm text-destructive">
                  {aiResult.note_ai || "Non sono riuscito ad analizzare il documento."}
                </div>
              ) : (
                <>
                  {aiResult.note_ai && (
                    <div className="p-3 bg-primary/5 rounded-lg text-xs sm:text-sm text-muted-foreground border border-primary/20">
                      {aiResult.note_ai}
                    </div>
                  )}

                  {/* ISEE */}
                  {aiResult.valore_isee && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Valore ISEE estratto</p>
                      <p className="text-xl font-bold">{aiResult.valore_isee.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                      {aiResult.scadenza_isee && <p className="text-xs text-muted-foreground mt-1">Scadenza: {aiResult.scadenza_isee}</p>}
                    </div>
                  )}

                  {/* Composizione nucleo */}
                  {aiResult.nucleo_familiare && aiResult.nucleo_familiare.length > 0 && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Nucleo Familiare ({aiResult.nucleo_familiare.length} componenti)</p>
                      <div className="space-y-1.5">
                        {aiResult.nucleo_familiare.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-sm gap-2">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{p.nome} {p.cognome}</span>
                              {p.relazione && <Badge variant="outline" className="ml-1 text-[10px]">{p.relazione}</Badge>}
                            </div>
                            {p.data_nascita && <span className="text-xs text-muted-foreground flex-shrink-0">{p.data_nascita}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Age distribution */}
                  {(aiResult.numero_adulti || aiResult.bambini_0_3 || aiResult.bambini_6_12 || aiResult.ragazzi_13_17 || aiResult.anziani) && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Composizione per fasce d'eta</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-xs">
                        {aiResult.numero_adulti > 0 && <div>Adulti: <span className="font-medium">{aiResult.numero_adulti}</span></div>}
                        {aiResult.bambini_0_3 > 0 && <div>0-3 anni: <span className="font-medium">{aiResult.bambini_0_3}</span></div>}
                        {aiResult.bambini_4_5 > 0 && <div>4-5 anni: <span className="font-medium">{aiResult.bambini_4_5}</span></div>}
                        {aiResult.bambini_6_12 > 0 && <div>6-12 anni: <span className="font-medium">{aiResult.bambini_6_12}</span></div>}
                        {aiResult.ragazzi_13_17 > 0 && <div>13-17 anni: <span className="font-medium">{aiResult.ragazzi_13_17}</span></div>}
                        {aiResult.anziani > 0 && <div>Anziani: <span className="font-medium">{aiResult.anziani}</span></div>}
                        {aiResult.disabili > 0 && <div>Disabili: <span className="font-medium">{aiResult.disabili}</span></div>}
                      </div>
                    </div>
                  )}

                  {/* Address */}
                  {(aiResult.indirizzo || aiResult.comune || aiResult.nome_referente) && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Dati anagrafici</p>
                      <div className="space-y-1 text-sm">
                        {aiResult.nome_referente && <div>Nome: <span className="font-medium">{aiResult.nome_referente}</span></div>}
                        {aiResult.codice_fiscale && <div>CF: <span className="font-mono font-medium text-xs">{aiResult.codice_fiscale}</span></div>}
                        {aiResult.indirizzo && <div>Indirizzo: <span className="font-medium">{aiResult.indirizzo}</span></div>}
                        {aiResult.comune && <div>Comune: <span className="font-medium">{aiResult.comune}</span></div>}
                      </div>
                    </div>
                  )}

                  <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                    <Button variant="outline" onClick={() => setAiDialogOpen(false)} className="w-full sm:w-auto">
                      Chiudi (non applicare)
                    </Button>
                    <Button onClick={handleApplyAiData} className="w-full sm:w-auto" data-testid="apply-ai-data-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Applica dati alla famiglia
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Access Dialog */}
      <Dialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crea Accesso Portale Famiglia</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Crea le credenziali per permettere a <b>{famiglia?.nome_referente}</b> di accedere al portale famiglia dove potra vedere le distribuzioni ricevute e caricare documenti.
            </p>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="email@esempio.it"
                value={accessData.email}
                onChange={(e) => setAccessData({ ...accessData, email: e.target.value })}
                data-testid="access-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="text"
                placeholder="Password iniziale"
                value={accessData.password}
                onChange={(e) => setAccessData({ ...accessData, password: e.target.value })}
                data-testid="access-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleCreateAccess} disabled={creatingAccess} data-testid="confirm-access-btn">
              {creatingAccess ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Crea Accesso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamigliaDetailPage;
